import React, { useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { COLOR } from '@src/styles/color';
import { StyledActionWarning } from './index.style';

const CustomActionLeavePage = ({
  navigate,
  when,
  isProjectConverting,
  shouldBlockNavigation,
  onLeavePage,
  onCancel,
  isAutoSave = false,
}) => {
  const { t } = useTranslation();
  const [modalVisible, updateModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLocation, updateLastLocation] = useState();
  const [confirmedNavigation, updateConfirmedNavigation] = useState(false);

  const showModal = (location) => {
    updateModalVisible(true);
    updateLastLocation(location);
  };

  const closeModal = (cb) => {
    updateModalVisible(false);
    if (cb) {
      cb();
    }
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
      showModal(nextLocation);
      return false;
    }
    return true;
  };
  const handleConfirmNavigationClick = () => {
    setIsLoading(true);
    onLeavePage((isSavedProjectSuccess) => {
      closeModal();
      setIsLoading(false);
      if (lastLocation && isSavedProjectSuccess) {
        updateConfirmedNavigation(true);
      }
    });
  };
  const handleCancel = () => {
    closeModal();
    if (lastLocation) {
      updateConfirmedNavigation(true);
      onCancel();
    }
  };

  const handleStayOnPage = () => {
    closeModal();
    onCancel();
  };

  const handleConfirmCancelSynthesisProject = () => {
    onLeavePage(() => {
      closeModal();
      setIsLoading(false);
      if (lastLocation) {
        updateConfirmedNavigation(true);
      }
    });
  };

  useEffect(() => {
    if (confirmedNavigation) {
      navigate(lastLocation.pathname);
      updateConfirmedNavigation(false);
    }
  }, [confirmedNavigation]);

  const renderUnsavedChangesActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleCancel}
      >
        {t('stayOnPage')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleConfirmNavigationClick}
      >
        {isLoading && (
          <CircularProgress
            size={20}
            sx={{ color: COLOR.darkGray, marginRight: '8px' }}
          />
        )}
        <span>{t('leaveAnyway')}</span>
      </Button>
    </StyledActionWarning>
  );

  const renderAutoSaveActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleCancel}
      >
        {t('leave')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleStayOnPage}
      >
        <span>{t('keepOnSynthesisProject')}</span>
      </Button>
    </StyledActionWarning>
  );

  const renderCancelSynthesisProjectActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleConfirmCancelSynthesisProject}
      >
        {t('cancelSynthesisProject')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleStayOnPage}
      >
        <span>{t('keepOnSynthesisProject')}</span>
      </Button>
    </StyledActionWarning>
  );

  const renderActions = () => {
    if (isProjectConverting) return renderCancelSynthesisProjectActions();
    if (isAutoSave) return renderAutoSaveActions();
    return renderUnsavedChangesActions();
  };

  const renderDescription = () => {
    if (isProjectConverting) return t('cancelSynthesisProjectProgress');
    if (isAutoSave) return t('autoSaveLeaveDescription');
    return t('unsavedChangesDescription');
  };

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      <NotificationDialogV3
        name="unsaved-changes"
        open={modalVisible}
        variant="none"
        title={
          isProjectConverting || isAutoSave
            ? t('noteV3')
            : t('unsavedChangesTitle')
        }
        padding="12px 24px"
        paddingHeader="8px 24px"
        description={renderDescription()}
        onClose={() => updateModalVisible(false)}
        actionComponent={renderActions()}
      />
    </>
  );
};

export default CustomActionLeavePage;
